import React, { useEffect, useState, Fragment } from 'react';
import {
  Typography,
  Grid,
  makeStyles,
  useMediaQuery,
  Card,
  CardContent,
  CardActions,
  LaunchIcon,
} from '@rbx/ui';
import styles from '@modules/ui/shared/utils/styles';
import { useRobloxAuthentication } from '@rbx/auth';
import AddToInventoryButton from '@modules/ui/markdown/components/AddToInventoryButton';
import DynamicallyRouted from '@modules/miscellaneous/common/components/DynamicallyRouted';
import UseStudioButton from '@modules/useStudio/components/UseStudioButton';
import { useTranslation } from '@rbx/intl';
import { isInteractKey } from '@modules/layout/layout/utils/keyboardNavigationHandler';

const defaultDescriptionHeight = 100;
const useBrowseSampleCardStyles = makeStyles()((theme) => ({
  title: {
    paddingBottom: '8px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    ...styles.textClamp(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  description: {
    marginTop: '8px',
    marginBottom: 50,
    overflowY: 'auto',
    textOverflow: 'ellipsis',
    ...styles.textClamp(4),
    height: defaultDescriptionHeight,
  },
  focusedDescription: {
    minHeight: defaultDescriptionHeight,
  },
  gridContainerStyle: {
    width: '100%',
    [theme.breakpoints.up('Medium')]: {
      height: 230 + 8 * 2,
    },
  },
  cardStyle: {
    height: '100%',
    '&:hover, &:focus, &:active': {
      zIndex: 99,
      boxShadow: theme.shadows[8],
    },
  },
  cardActions: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'transparent',
    opacity: 0,
    transition: 'opacity 0.1s ease-in-out',
    visibility: 'hidden',
  },
  focusedCardActions: {
    opacity: 1,
    visibility: 'visible',
  },
}));

export interface BrowseSampleCardProps {
  description: string;
  title: string;
  href: string;
  assetId: number;
}

export default function BrowseSampleCard({
  description,
  title,
  href,
  assetId,
}: BrowseSampleCardProps) {
  const [userId, setUserId] = useState<number | undefined>(undefined);
  const { translate } = useTranslation();
  const { user: authenticatedUser } = useRobloxAuthentication();
  const [isHoverOnItemCard, setIsHoverOnItemCard] = useState<boolean>(false);
  const [isAssetOwned, setIsAssetOwned] = useState<boolean>(false);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('Medium'));
  const supportsHover = useMediaQuery('(hover: hover)');

  const {
    classes: {
      description: descriptionStyle,
      focusedDescription,
      title: titleStyle,
      gridContainerStyle,
      cardStyle,
      cardActions,
      focusedCardActions,
    },
    cx,
  } = useBrowseSampleCardStyles();

  useEffect(() => {
    if (authenticatedUser !== null && authenticatedUser?.id) {
      setUserId(authenticatedUser.id);
    }
  }, [authenticatedUser, isHoverOnItemCard, isMobile]);

  return (
    <Grid item XLarge={4} Large={6} classes={{ root: gridContainerStyle }}>
      <Card
        onMouseEnter={() => setIsHoverOnItemCard(true)}
        onKeyDown={(e) => {
          if (isInteractKey(e)) {
            e.stopPropagation();
            e.preventDefault();
            setIsHoverOnItemCard(true);
          }
        }}
        onMouseLeave={() => setIsHoverOnItemCard(false)}>
        <DynamicallyRouted.CardActionArea href={href}>
          <CardContent classes={{ root: cardStyle }}>
            <Typography variant='h4' component='h4' classes={{ root: titleStyle }}>
              {title}
            </Typography>
            <Typography
              variant='body2'
              color='secondary'
              component='p'
              classes={{ root: cx(descriptionStyle, { [focusedDescription]: isHoverOnItemCard }) }}>
              {description}
            </Typography>
          </CardContent>
        </DynamicallyRouted.CardActionArea>
        <CardActions
          className={cx(cardActions, {
            [focusedCardActions]: !supportsHover || isHoverOnItemCard,
          })}>
          {(!supportsHover || isHoverOnItemCard) && assetId && (
            <Fragment>
              <UseStudioButton
                variant='button'
                buttonProps={{
                  variant: 'text',
                  color: 'primaryBrand',
                  size: 'small',
                  startIcon: <LaunchIcon />,
                }}
                buttonStyle={{
                  margin: 0,
                }}
                assetId={assetId}>
                {translate('Button.OpenInStudio')}
              </UseStudioButton>
              <AddToInventoryButton
                userId={userId}
                assetId={assetId}
                title={title}
                isAssetOwned={isAssetOwned}
                setIsAssetOwned={setIsAssetOwned}
              />
            </Fragment>
          )}
        </CardActions>
      </Card>
    </Grid>
  );
}
